import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { Popover, Tabs } from "antd";
import { useMapStore } from "stores/map";
import styled from "styled-components";

import { PlaceLocation } from "api/map";

import { ToolbarButton } from "components/base";
import Navigate from "components/icons/Navigate";

import { useAvailableLandgrids } from "../hooks/useDataSourceAvailability";
import { useMapContext } from "../hooks/useMapContext";
import { resolveJumpCamera } from "../utils/resolveJumpCamera";
import LatLonSelector from "./LatLonSelector";
import MapTownshipSelector from "./MapTownshipSelector";
import SavedMapLocationSelector from "./SavedMapLocationSelector";
import UsLandgridSelector from "./UsLandgridSelector";
import WellJumpModal from "./WellJumpModal";
import WellJumpSelector from "./WellJumpSelector";

const WELL_KEY = "well";
const LAT_LON_KEY = "latLon";
const SAVED_KEY = "saved";
const TOWNSHIP_KEY = "township";
const US_LANDGRID_KEY = "usLandgrid";

// Ctrl+A must have exactly ONE owner: useHotkeys registers per instance and
// this component renders per map widget, so two widgets would open two
// dialogues whose focus() calls race. First to mount claims it; on release it
// is HANDED to the next waiter rather than merely freed, because a non-owner's
// effect has [] deps and never re-checks — without the handoff the shortcut
// died for every widget as soon as the owner unmounted.
let hotkeyOwner: Nullable<symbol> = null;
const hotkeyWaiters = new Map<symbol, (owns: boolean) => void>();

function useOwnsJumpHotkey(): boolean {
  const idRef = useRef<symbol>();
  if (!idRef.current) idRef.current = Symbol("go-to-township-play");
  const [owns, setOwns] = useState(false);

  useEffect(() => {
    const id = idRef.current;
    hotkeyWaiters.set(id, setOwns);

    if (hotkeyOwner === null) {
      hotkeyOwner = id;
      setOwns(true);
    }

    return () => {
      hotkeyWaiters.delete(id);
      if (hotkeyOwner !== id) return;

      hotkeyOwner = null;
      // No setOwns(false): this instance is unmounting, and setting state on it
      // warns in React 17 without changing anything.
      const next = hotkeyWaiters.entries().next();
      if (!next.done) {
        const [nextId, notify] = next.value;
        hotkeyOwner = nextId;
        notify(true);
      }
    };
  }, []);

  return owns;
}

export default function GoToTownshipPlay() {
  const setMapState = useMapStore((s) => s.setMapState);
  const { mapbox } = useMapContext();
  const landgrids = useAvailableLandgrids();

  const [navigateOpen, setNavigateOpen] = useState(false);
  const [navigateTab, setNavigateTab] = useState<Nullable<string>>(null);
  // Ctrl+A opens its own quick-entry dialogue rather than re-opening this
  // popover on the Well tab, matching Insights: the tab is the multi-well list,
  // Ctrl+A the single-well prompt. Both run the same useWellJump().
  const [wellJumpModalOpen, setWellJumpModalOpen] = useState(false);
  const wellJumpReturnFocusRef = useRef<Nullable<HTMLElement>>(null);

  const ownsHotkey = useOwnsJumpHotkey();

  // Stable identity on purpose: react-hotkeys-hook deep-compares its options but
  // falls through to === for functions, so an inline arrow tears down and re-adds
  // the document listeners on every render.
  const shouldIgnoreHotkeyEvent = useCallback(
    (event: KeyboardEvent) => {
      // Ignore rather than `enabled: false`, which in 4.6.2 still
      // preventDefaults and swallows select-all.
      if (!ownsHotkey) return true;
      // contenteditable is NOT checked — the library already returns on it
      // before preventDefault. Only the canvas-hosted grids need naming: they
      // are not form tags, so its input/textarea/select exclusion misses them.
      const target = event.target as Maybe<HTMLElement>;
      return Boolean(
        target?.closest?.(
          '[role="grid"], [role="treegrid"], .dvn-scroller, [class*="gdg-"]'
        )
      );
    },
    [ownsHotkey]
  );

  // Registered unconditionally, before the `if (!mapbox) return null` below,
  // so this hook's order never changes across renders.
  useHotkeys(
    "ctrl+a, meta+a",
    () => {
      // Captured here, not inside the dialogue: focus is still unambiguously on
      // whatever the user was using, and capturing after mount races antd's
      // focus trap. A shortcut-opened dialogue has no trigger element, so antd's
      // focusTriggerAfterClose has nothing to restore to.
      wellJumpReturnFocusRef.current = document.activeElement as Nullable<HTMLElement>;
      setWellJumpModalOpen(true);
    },
    {
      preventDefault: true, // suppress native select-all when the shortcut fires
      // The binding is at document level and live whenever any map widget is
      // mounted, including while the user works in a canvas-hosted grid with its
      // own Cmd+A. `enabled` cannot fix that: in 4.6.2 maybePreventDefault runs
      // BEFORE the enabled check, so a disabled hotkey still swallows the
      // keystroke. ignoreEventWhen returns earlier than both.
      //
      // The selector names the GRID hosts and NOT `canvas`: mapbox renders into
      // <canvas class="mapboxgl-canvas">, so `closest("canvas")` would exclude
      // the one surface this shortcut exists for.
      ignoreEventWhen: shouldIgnoreHotkeyEvent
    },
    [ownsHotkey] // enableOnFormTags left off (library default), so native
    // select-all keeps working in every input, including this panel's own
    // Well tab paste box
  );

  function goToPointAndHighlightPolygon(data, zoom = 5) {
    const place = data;
    if (!place || !mapbox) {
      return;
    }

    if (place.extent) {
      const bounds: [[number, number], [number, number]] = [
        [place.extent.minx, place.extent.miny],
        [place.extent.maxx, place.extent.maxy]
      ];
      const fitBoundsOptions = place.exactBounds
        ? {
            padding: 0,
            linear: true,
            maxZoom: 18
          }
        : {
            padding: 40,
            duration: 1000
          };

      mapbox.fitBounds(bounds, fitBoundsOptions);

      const centerLng = (place.extent.minx + place.extent.maxx) / 2;
      const centerLat = (place.extent.miny + place.extent.maxy) / 2;
      const newMapState = {
        lng: centerLng,
        lat: centerLat,
        zoom: mapbox.getZoom()
      };
      setMapState(newMapState);
      return;
    }

    const centroid = place.Centroid;
    mapbox.flyTo({
      center: [centroid.coordinates[0], centroid.coordinates[1]],
      zoom,
      essential: true
    });
    const newMapState = {
      lng: centroid.coordinates[0],
      lat: centroid.coordinates[1],
      zoom: zoom
    };
    setMapState(newMapState);
  }

  /**
   * Moves this map to a place the location lookup resolved. The camera is worked out and
   * rounded first, then flown to, so the numbers given to the map and the numbers recorded
   * here are the same ones. The same place gives the same rounded camera every time, and
   * the destination is that camera whichever way the map gets there.
   *
   * The move is animated on purpose, and the animation has two consequences worth knowing.
   * The debounced moveend handler overwrites what is recorded here with whatever the map
   * reports once the move settles, and that agrees with the destination only while the
   * flight is left to finish: a user who scrolls or drags mid-flight stops it where they
   * are, and that is what gets recorded. Taking the map back off them would be worse.
   * Saving reads the live map the same way, so a save made before the flight settles
   * records the camera the map is passing through rather than the place jumped to.
   */
  function jumpToPlace(place: PlaceLocation, fallbackZoom: number) {
    // Thrown, like a place with no camera: a silent return here would leave the caller
    // closing the panel on a jump that never happened and saying nothing about it.
    if (!mapbox) {
      throw new Error("The map is not ready to jump to a location");
    }
    const camera = resolveJumpCamera(mapbox, place, fallbackZoom);
    // A place with no position to aim at is a failed jump. Thrown, because the caller reads
    // a failure here as "Unable to find location" and leaves the panel open on it.
    if (!camera) {
      throw new Error("The location has no position to jump to");
    }
    mapbox.flyTo({
      center: [camera.lng, camera.lat],
      zoom: camera.zoom,
      bearing: 0,
      pitch: 0,
      essential: true
    });
    setMapState({ lng: camera.lng, lat: camera.lat, zoom: camera.zoom });
  }

  if (!mapbox) {
    return null;
  }

  // A landgrid none of the organization's data sources come with has no tab here. Lat/lon
  // and saved locations need no grid data of their own, so both tabs are always offered.
  // Well is prepended unconditionally — it needs no landgrid data either, and
  // `activeTab` below falls back to `tabs[0].key`, so prepending makes it both
  // first and the default with no change to that logic.
  const tabs = [
    {
      key: WELL_KEY,
      label: "Well",
      children: (
        <WellJumpSelector
          closeMenu={() => setNavigateOpen(false)}
          isOpen={navigateOpen}
        />
      )
    },
    ...(landgrids.canada
      ? [
          {
            key: TOWNSHIP_KEY,
            label: "Township Range",
            children: (
              <MapTownshipSelector
                closeMenu={() => setNavigateOpen(false)}
                goToPointAndHighlightPolygon={goToPointAndHighlightPolygon}
              />
            )
          }
        ]
      : []),
    ...(landgrids.us
      ? [
          {
            key: US_LANDGRID_KEY,
            label: "US Landgrid",
            children: (
              <UsLandgridSelector
                closeMenu={() => setNavigateOpen(false)}
                jumpToPlace={jumpToPlace}
              />
            )
          }
        ]
      : []),
    {
      key: LAT_LON_KEY,
      label: "Lat/Long",
      children: (
        <LatLonSelector
          closeMenu={() => setNavigateOpen(false)}
          goToPointAndHighlightPolygon={goToPointAndHighlightPolygon}
        />
      )
    },
    {
      key: SAVED_KEY,
      label: "Saved",
      children: <SavedMapLocationSelector />
    }
  ];

  // Which tab is open has to be one the organization has, so it follows the tabs rather
  // than naming one of them. A tab the user chose is kept.
  const activeTab = tabs.find((tab) => tab.key === navigateTab)?.key ?? tabs[0].key;

  return (
    <>
      <WellJumpModal
        open={wellJumpModalOpen}
        onClose={() => {
          setWellJumpModalOpen(false);
          // Hand the keyboard back to the map.
          wellJumpReturnFocusRef.current?.focus?.();
        }}
      />
      <Popover
        arrowPointAtCenter
        content={
          <GoToContainer
            onKeyDown={(e) => {
              if (e.key === "Escape") setNavigateOpen(false);
            }}>
            <Tabs
              activeKey={activeTab}
              onTabClick={(key) => setNavigateTab(key)}
              items={tabs}
            />
          </GoToContainer>
        }
        onOpenChange={(v) => setNavigateOpen(v)}
        open={navigateOpen}
        placement="bottomRight"
        trigger="click">
        <ToolbarButton
          active={false}
          icon={<Navigate size={15} />}
          overflowLabel="Jump to Location"
          tooltipText="Jump to Location"
          styleKey="miniMapToolbar"
        />
      </Popover>
    </>
  );
}

const GoToContainer = styled.div`
  /* Widened from 420px when the Well tab made this a five-tab row — the tabs
     share the row evenly, so each one lost width as the count went up. */
  width: 468px;
  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: 1px solid #d9e1e2;
  }

  .ant-tabs-nav {
    margin: 0;
  }
  /* Two things, and only two, decide the space around and between the tabs: a fixed 20px
     between neighbours, and the rest of the row shared out into equal gaps before, between
     and after them. antd adds a margin of its own to every tab after the first, which would
     be a third and cannot come out even, so it is taken off here. */
  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0;
  }
  .ant-tabs-nav-list {
    width: 100%;
    gap: 20px;
    justify-content: space-evenly;
  }

  .ant-tabs-nav-list .ant-tabs-tab .ant-tabs-tab-btn {
    font-weight: bold;
    font-size: 1.4rem;
    margin: 0;
  }
  .ant-tabs .ant-tabs-content {
    height: 100%;
    padding: 20px 20px;
  }
  .ant-tabs-content {
    display: grid;
    overflow-y: auto;
    padding-bottom: 10px;
    width: 100%;
  }
`;
